import { useMemo } from 'react';
import { PageItem } from '../../types';

// Builds the list of items to render in the pagination strip:
//   first page, ... (gap), current ± siblings, ... (gap), last page.
// Collapses to a flat range when total pages fit without any gap.
export const usePageWindow = (currentPage: number, totalPages: number, siblingCount = 1): PageItem[] => {
  return useMemo(() => {
    if (totalPages <= 0) return [];

    // Total slots = first + last + current + (sibling*2) + (2 ellipsis) = 5 + siblingCount * 2.
    const totalSlots = 5 + siblingCount * 2;
    if (totalPages <= totalSlots) {
      return Array.from({ length: totalPages }, (_, i) => ({ kind: 'page', page: i + 1 }) as PageItem);
    }

    const leftSibling = Math.max(currentPage - siblingCount, 1);
    const rightSibling = Math.min(currentPage + siblingCount, totalPages);

    const showLeftGap = leftSibling > 2;
    const showRightGap = rightSibling < totalPages - 1;

    const items: PageItem[] = [];

    if (!showLeftGap && showRightGap) {
      const leftRange = 3 + siblingCount * 2;
      for (let i = 1; i <= leftRange; i++) items.push({ kind: 'page', page: i });
      items.push({ kind: 'ellipsis', id: 'gap-right' });
      items.push({ kind: 'page', page: totalPages });
    } else if (showLeftGap && !showRightGap) {
      items.push({ kind: 'page', page: 1 });
      items.push({ kind: 'ellipsis', id: 'gap-left' });
      const rightRange = 3 + siblingCount * 2;
      for (let i = totalPages - rightRange + 1; i <= totalPages; i++) items.push({ kind: 'page', page: i });
    } else if (showLeftGap && showRightGap) {
      items.push({ kind: 'page', page: 1 });
      items.push({ kind: 'ellipsis', id: 'gap-left' });
      for (let i = leftSibling; i <= rightSibling; i++) items.push({ kind: 'page', page: i });
      items.push({ kind: 'ellipsis', id: 'gap-right' });
      items.push({ kind: 'page', page: totalPages });
    } else {
      for (let i = 1; i <= totalPages; i++) items.push({ kind: 'page', page: i });
    }

    return items;
  }, [currentPage, totalPages, siblingCount]);
};
